import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { PageHero, SectionLabel, EnquiryBanner } from "@/components/site/Sections";
import { TIMELINE } from "@/lib/data";

const VALUES = [
  { title: "Trust", body: "Earned over five decades, order after order. Our word is our reputation." },
  { title: "Integrity", body: "Honest advice on grade, availability and value — even when it isn't the biggest sale." },
  { title: "Consistency", body: "Dependable stock and steady supply so your production never waits on paper." },
  { title: "Relationships", body: "We invest in long-term partnerships with both our mills and our customers." },
];

const About = () => (
  <div data-testid="about-page">
    <PageHero
      label="About Mysore Paper"
      title="Serving the paper trade since 1970."
      subtitle="A multi-generational family business built on trust, deep market knowledge and relationships that last."
    />

    <section className="relative z-10 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="overflow-hidden rounded-sm">
          <img
            src="https://images.unsplash.com/photo-1515697203610-d0513edf9cb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwcGFwZXIlMjByb2xsc3xlbnwwfHx8fDE3ODQwMzk0NjZ8MA&ixlib=rb-4.1.0&q=85"
            alt="Warehouse stocked with paper reels"
            className="w-full h-[300px] lg:h-[480px] object-cover"
          />
        </Reveal>
      </div>
    </section>

    <section className="relative z-10 py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal><SectionLabel>The narrative</SectionLabel></Reveal>
        </div>
        <div className="lg:col-span-8 space-y-8 text-lg text-ink-soft leading-relaxed">
          <Reveal>
            <p>
              In 1970, <span className="text-forest font-medium">Meghraj Jain</span> founded Mysore
              Paper Suppliers Corporation with a simple belief: that a paper trading business is
              only as good as the trust it builds. He set out to be the name that printers and
              publishers could rely on — for the right grade, at the right time, at fair value.
            </p>
          </Reveal>
          <Reveal>
            <p>
              As the business grew, the second generation, <span className="text-forest font-medium">Dinesh Jain</span>,
              carried that promise forward. He deepened relationships with mills, broadened the range
              of paper and board grades on offer, and strengthened the company&rsquo;s reputation across
              the trade.
            </p>
          </Reveal>
          <Reveal>
            <p>
              Today, Dinesh Jain works alongside the third generation,
              <span className="text-forest font-medium"> Milan Jain</span>. Together they combine
              hard-earned experience with a modern, responsive approach — bringing quicker answers,
              clearer communication and the same consistency the family has been known for since 1970.
            </p>
          </Reveal>
          <Reveal>
            <p className="text-forest font-serif text-2xl lg:text-3xl leading-snug">
              &ldquo;We don&rsquo;t just sell paper. We help our customers source with confidence &mdash; and we&rsquo;ve been
              doing it for three generations.&rdquo;
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="relative z-10 py-20 lg:py-28 bg-paper-soft/50 border-y border-paper-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <Reveal><SectionLabel>Our timeline</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest leading-tight">From 1970 to today.</h2>
          </Reveal>
        </div>
        <div className="grid gap-10 lg:grid-cols-3">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.1}>
              <div className="border-t-2 border-forest pt-8">
                <p className="font-serif text-6xl text-forest tracking-tight">{t.year}</p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.24em] text-gold">{t.person}</p>
                <h3 className="mt-4 font-serif text-2xl text-forest">{t.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative z-10 py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <Reveal><SectionLabel>What we stand for</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest leading-tight">
              Values that carry across generations.
            </h2>
          </Reveal>
        </div>
        <Stagger className="grid gap-px bg-paper-border border border-paper-border rounded-sm overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <StaggerItem key={v.title} className="bg-paper p-9">
              <h3 className="font-serif text-3xl text-forest">{v.title}</h3>
              <p className="mt-4 text-sm text-ink-soft leading-relaxed">{v.body}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <EnquiryBanner />
  </div>
);

export default About;
